import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Trophy, ChevronUp, ChevronDown, Search } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;

const CLASS_ICON_SLUG = {
  'Guerrero':                'warrior',
  'Paladín':                 'paladin',
  'Cazador':                 'hunter',
  'Pícaro':                  'rogue',
  'Sacerdote':               'priest',
  'Caballero de la Muerte':  'deathknight',
  'Chamán':                  'shaman',
  'Mago':                    'mage',
  'Brujo':                   'warlock',
  'Druida':                  'druid',
};

function ClassIcon({ className, size = 18 }) {
  const slug = CLASS_ICON_SLUG[className];
  if (!slug) return null;
  return (
    <img
      src={`https://wow.zamimg.com/images/wow/icons/small/classicon_${slug}.jpg`}
      alt={className}
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      style={{ borderRadius: 3, verticalAlign: 'middle', marginRight: 6, flexShrink: 0 }}
    />
  );
}

const ALL_CLASSES = ['Caballero de la Muerte', 'Paladín', 'Cazador', 'Mago', 'Brujo', 'Sacerdote', 'Chamán', 'Druida', 'Guerrero', 'Pícaro'];

function GearBar({ score }) {
  const max = 284;
  const pct = Math.min(100, (score / max) * 100);
  return (
    <div className="gs-bar-wrap">
      <div className="gs-bar-track">
        <div className="gs-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="gs-score">{score.toLocaleString()}</span>
    </div>
  );
}

function CharacterSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  const handleChange = useCallback((e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/armory/search?q=${encodeURIComponent(val.trim())}`);
        setResults(data);
        setOpen(data.length > 0);
      } catch { setResults([]); setOpen(false); }
    }, 300);
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (name) => { setQuery(''); setResults([]); setOpen(false); navigate(`/armeria/${name}`); };

  return (
    <div className="armory-search-wrap" ref={wrapRef}>
      <div className="armory-search-input-wrap">
        <Search size={15} className="armory-search-icon" />
        <input
          className="armory-search-input"
          placeholder="Buscar personaje..."
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
        />
      </div>
      {open && (
        <ul className="armory-search-dropdown">
          {results.map((r) => (
            <li key={r.name} className="armory-search-item" role="option" aria-selected="false" onClick={() => handleSelect(r.name)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelect(r.name); }}>
              <span className="armory-search-name" style={{ color: r.class_color }}>{r.name}</span>
              <span className="armory-search-meta">{r.class_name} · Nv. {r.level}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return null;
  return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
}

export default function ArmeriaPage() {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [sortField, setSortField] = useState('gear_score');
  const [sortDir, setSortDir] = useState('desc');
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/armory/top?limit=12`)
      .then(({ data }) => setCharacters(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const filtered = characters
    .filter(c => !filter || c.class_name === filter)
    .sort((a, b) => {
      const va = a[sortField] ?? 0;
      const vb = b[sortField] ?? 0;
      return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });


  return (
    <PageWrapper scrollable>
      <SEOMeta
        title="Armería | Mejores personajes WotLK"
        description="Consulta el ranking de personajes de Frostbound. Gear score, clase, gremio y equipamiento de los mejores jugadores del servidor WoW 3.3.5a."
        canonical="/armeria"
      />
      <Navbar />

      <main className="armory-main">
        {/* Header */}
        <div className="dl-header anim-fade-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="shop-title" data-testid="armory-heading">
            <Shield size={30} className="shop-title-icon" />
            ARMERÍA
          </h1>
          <p className="shop-subtitle">
            Los héroes más poderosos de{' '}
            <span className="cyan-highlight">Azeroth.</span>
          </p>
        </div>

        {/* Search */}
        <div className="anim-fade-up" style={{ animationDelay: '0.15s', position: 'relative', zIndex: 1000, width: '100%' }}>
          <CharacterSearch />
        </div>

        {/* Class Filter */}
        <div className="armory-filters anim-fade-up" style={{ animationDelay: '0.2s' }} data-testid="armory-filters">
          {ALL_CLASSES.map((cls) => (
            <button
              key={cls}
              className={`filter-btn ${filter === cls ? 'filter-btn--active' : ''}`}
              onClick={() => setFilter(prev => prev === cls ? null : cls)}
              data-testid={`armory-filter-${cls.toLowerCase().replace(' ', '-')}`}
            >
              {cls}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="armory-table-wrap anim-fade-up" style={{ animationDelay: '0.3s' }}>
          {loading ? (
            <div className="armory-loading" data-testid="armory-loading">Invocando héroes...</div>
          ) : (
            <table className="armory-table" data-testid="armory-table">
              <thead>
                <tr>
                  <th className="armory-th armory-th--rank">#</th>
                  <th className="armory-th">
                    <button className="sort-btn" onClick={() => handleSort('name')}>
                      PERSONAJE <SortIcon field="name" sortField={sortField} sortDir={sortDir} />
                    </button>
                  </th>
                  <th className="armory-th armory-th--hide-sm">CLASE</th>
                  <th className="armory-th armory-th--hide-md">RAZA</th>
                  <th className="armory-th armory-th--hide-md">HERMANDAD</th>
                  <th className="armory-th">
                    <button className="sort-btn" onClick={() => handleSort('gear_score')}>
                      EQUIPO (GS) <SortIcon field="gear_score" sortField={sortField} sortDir={sortDir} />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((char, i) => (
                  <tr
                    key={char.name}
                    className={`armory-row ${i % 2 === 0 ? 'armory-row--even' : ''}`}
                    data-testid={`armory-row-${char.name}`}
                  >
                    <td className="armory-td armory-td--rank">
                      {i === 0 && <Trophy size={14} style={{ color: '#FFD700', marginRight: 4 }} />}
                      {i === 1 && <Trophy size={14} style={{ color: '#C0C0C0', marginRight: 4 }} />}
                      {i === 2 && <Trophy size={14} style={{ color: '#CD7F32', marginRight: 4 }} />}
                      {i > 2 && <span style={{ color: 'rgba(255,255,255,0.35)' }}>{i + 1}</span>}
                    </td>
                    <td className="armory-td">
                      <button
                        className="char-name armory-char-link"
                        style={{ color: char.class_color, textShadow: `0 0 10px ${char.class_color}55`, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                        data-testid={`char-name-${char.name}`}
                        onClick={() => navigate(`/armeria/${char.name}`)}
                      >
                        <ClassIcon className={char.class_name} size={16} />
                        {char.name}
                      </button>
                    </td>
                    <td className="armory-td armory-td--hide-sm">
                      <span className="char-class" style={{ color: char.class_color }}>
                        {char.class_name}
                      </span>
                    </td>
                    <td className="armory-td armory-td--hide-md armory-td--muted">{char.race}</td>
                    <td className="armory-td armory-td--hide-md armory-td--muted">{char.guild}</td>
                    <td className="armory-td">
                      <GearBar score={char.gear_score} />
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="armory-empty" data-testid="armory-empty">
                      No hay personajes de esta clase aún.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <p className="shop-notice" style={{ marginTop: 20 }}>
          Actualizado cada hora · Puntuación de equipo basada en ilvl promedio
        </p>
      </main>

      <Footer />
    </PageWrapper>
  );
}
